/* ==========================================================================
   <product-size-guide>
   Controls the verified product size guide dialog and selected-size context.
   ========================================================================== */

class ProductSizeGuide extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-size-guide-open]');
    this.dialog = this.querySelector('[data-size-guide-dialog]');
    if (!this.trigger || !this.dialog) return;
    if (typeof this.dialog.showModal !== 'function') return;
    const panel = this.querySelector('[data-size-guide-content]');
    if (panel) {
      this.dialog.appendChild(panel);
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.dialog.querySelector('[data-size-guide-close]').hidden = false;
    }

    this._onOpen = this._onOpen || this._open.bind(this);
    this._onDialogClick = this._onDialogClick || this._handleDialogClick.bind(this);
    this._onClose = this._onClose || this._handleClose.bind(this);
    this._onKeyDown = this._onKeyDown || ((event) => {
      if (event.key === 'Escape' && this.dialog.open) {
        event.preventDefault();
        this.dialog.close();
      }
    });

    this.trigger.addEventListener('click', this._onOpen);
    this.dialog.addEventListener('click', this._onDialogClick);
    this.dialog.addEventListener('close', this._onClose);
    this.dialog.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this._onOpen);
    this.dialog?.removeEventListener('click', this._onDialogClick);
    this.dialog?.removeEventListener('close', this._onClose);
    this.dialog?.removeEventListener('keydown', this._onKeyDown);
    if (this.dialog?.open) {
      this.dialog.close();
      window.theme?.unlockScroll?.('size-guide');
    }
  }

  _open(event) {
    event?.preventDefault();
    if (this.dialog.open) return;
    this._opener = this.trigger;
    this._highlightSelectedSize();
    this.dialog.showModal();
    window.theme?.lockScroll?.('size-guide');
    this._dispatchOpenEvent();
  }

  _dispatchOpenEvent() {
    try {
      const picker = this.closest('variant-picker');
      const metadata = JSON.parse(this.closest('.pdp')
        ?.querySelector('[data-product-page-metadata]')?.textContent || '{}');
      const sizeInputName = `options[${this.dataset.sizeOptionName}]`;
      const size = Array.from(picker?.querySelectorAll('input[type="radio"]:checked') || [])
        .find(input => input.name === sizeInputName)?.value;
      this.dispatchEvent(new CustomEvent('pdp:size-guide-open', {
        bubbles: true,
        detail: {
          ...metadata,
          variantId: picker?.querySelector('input[name="id"]')?.value,
          size,
        },
      }));
    } catch (_) {
      // Optional analytics context must never affect the size guide.
    }
  }

  _handleDialogClick(event) {
    const closeButton = event.target?.closest?.('[data-size-guide-close]');
    if (event.target === this.dialog || closeButton) this.dialog.close();
  }

  _handleClose() {
    window.theme?.unlockScroll?.('size-guide');
    this._opener?.focus();
  }

  _highlightSelectedSize() {
    const sizeInputName = `options[${this.dataset.sizeOptionName}]`;
    const selectedSize = Array.from(this.closest('variant-picker')
      ?.querySelectorAll('input[type="radio"]:checked') || [])
      .find(input => input.name === sizeInputName)?.value;

    this.dialog.querySelectorAll('[data-size-guide-size]').forEach((row) => {
      row.toggleAttribute(
        'data-selected',
        Boolean(selectedSize) && row.dataset.sizeGuideSize === selectedSize
      );
    });
  }
}

if (!customElements.get('product-size-guide')) {
  customElements.define('product-size-guide', ProductSizeGuide);
}
